import { EMAIL, IN_APP, SMS, WEB_PUSH, WHATS_APP, WhatsApp } from 'constants/messageMediums';
import { BASE, BLAST, TemplateType, TRIGGERED } from 'constants/templateTypes';
import { AllMessageMedium, CampaignType } from 'modules/domains/campaign';
import { TemplateType as TT } from 'modules/templateEditor/common';
import { getFromNow } from 'utils/date';
import { FetchTemplatesProps, TemplateWithDetails } from './types';

// for each template, we need the name and avatarUrl of the member who
// either most recently edited (preferred) or created the template (fall-back).
// by the time we get to this function, which to show has already been established
// via the selectTemplatesByQuery selector and stored as "memberId"

// however, depending on the templates currently being displayed on the screen
// some of those templates might have the same memberId multiple times.

// which means want a unique list of memberIds to send to the backend.
export const getUniqueMemberIds = (templates: TemplateWithDetails[]): string[] => {
  return templates.reduce<string[]>((acc, template) => {
    if (!template.memberId) {
      return acc;
    }
    if (acc.includes(template.memberId)) {
      return acc;
    }

    return [...acc, template.memberId];
  }, []);
};

export const hasRenderedTemplatesInRedux = (templates: any[], queriedTemplateIds: any[]) => {
  if (templates.length === 0) return false;

  return templates.length === queriedTemplateIds.length;
};

// used to create the query prop by which templates
// for the createWizard are stored and selected in redux
export const getTemplatesLocationQueryString = ({
  orderBy,
  sorting,
  templateType,
  messageMedium,
  filter,
  page,
  pageSize,
  whatsAppTemplateType,
}: FetchTemplatesProps) =>
  `${orderBy}-${sorting}-${templateType}-${messageMedium}-${filter}-${page}-${pageSize}${
    whatsAppTemplateType ? `-${whatsAppTemplateType}` : ''
  }`;

export const formatTemplatePickerTemplate = (
  template: Record<string, any> & {
    updatedAtRaw: number;
    updatedBy?: string;
    creatorUserId: string;
  },
) => {
  // human readable date displayed on the template tile
  const updatedFromNow = getFromNow(template.updatedAtRaw);

  // id of the member to display on the tile. defaults to the template's creator,
  // if the data for the last member to update the template does not exist.
  const memberId = template.updatedBy || template.creatorUserId;

  return {
    ...template,
    updatedFromNow,
    memberId,
  };
};

/**
 * This seems like overkill but we're having some edge case error slip through and crash
 * the picker in certain client accounts. So attempting to add extra safeguards to see if we
 * can fix the unknown edge case somehow slipping an undefined into the avatar
 */
export function getNameDetails(firstName?: string, lastName?: string) {
  let safeFirstName = '';
  let safeLastName = '';

  if (typeof firstName === 'string') {
    safeFirstName = firstName.trim();
  }

  if (typeof lastName === 'string') {
    safeLastName = lastName.trim();
  }

  return {
    firstName: safeFirstName,
    lastName: safeLastName,
    lastInitial: safeLastName[0] ? safeLastName[0] : '',
  };
}

// formats the way the template type name is displayed.
export const formatMessageMedium = (messageMedium: string) => {
  switch (messageMedium) {
    case IN_APP:
      return 'in-app';
    case SMS:
      return 'SMS';
    case WEB_PUSH:
      return 'web push';
    case WHATS_APP:
      return 'WhatsApp';
    default:
      return messageMedium.toLowerCase();
  }
};

// empty state for tiles will have different text
// returns an array for the text of the two lines.
export const setEmptyTileStateText = (messageMedium = '', templateType = '', query = '') => {
  const formattedMessageMedium = formatMessageMedium(messageMedium);
  const formattedTemplateType = templateType.toLowerCase();

  // empty state for when a search was done via a filter
  // but nothing was found
  const noTemplatesFoundFromFilteredQuery = query.length > 0;

  if (noTemplatesFoundFromFilteredQuery) {
    return {
      text: `No ${formattedMessageMedium} ${formattedTemplateType} templates found.`,
      subtext: 'Try a different search.',
    };
  }

  // Base messages
  if (templateType === BASE) {
    const subtext =
      messageMedium === EMAIL
        ? `Create a new email base template by starting from a blank template, importing HTML, or cloning an Iterable provided quick template.`
        : `Create a new ${formattedMessageMedium} ${formattedTemplateType} template by starting from a blank template.`;

    return {
      text: `No ${formattedMessageMedium} ${formattedTemplateType} templates in your project yet.`,
      subtext,
    };
  }

  // Campaign messages
  if (templateType === BLAST || templateType === TRIGGERED) {
    return {
      text: `No ${formattedMessageMedium} ${formattedTemplateType} messages in your project yet.`,
      subtext: `Create a new ${formattedMessageMedium} campaign with content and design for campaign messages to show up here.`,
    };
  }

  // WORKFLOW messages
  return {
    text: `No ${formattedMessageMedium} ${formattedTemplateType} messages in your project yet.`,
    subtext: `Create a new workflow with send ${formattedMessageMedium} node for workflow messages to show up here.`,
  };
};

/**
 * Template types are only displayed for triggered WhatsApp campaigns
 */
export const getDisplayTemplateType = ({
  messageMedium,
  campaignType,
  templateType,
}: {
  messageMedium: AllMessageMedium;
  campaignType?: CampaignType;
  templateType: TT;
}) =>
  // eslint-disable-next-line no-nested-ternary
  messageMedium === WHATS_APP && campaignType === CampaignType.ApiTriggered
    ? templateType === TemplateType.BusinessInitiated
      ? 'Business-initiated'
      : 'User-initiated'
    : '';

export const parseTemplateTypeFilterSelection = ({
  messageMedium,
  campaignType,
  templateTypeFilter,
}: {
  messageMedium: AllMessageMedium;
  campaignType?: CampaignType;
  templateTypeFilter: TemplateType[];
}) => {
  // case: not WhatsApp campaign so no template types involved
  if (messageMedium !== WhatsApp) return undefined;

  if (campaignType === CampaignType.Blast) {
    // case: blast campaign only supports business-initiated templates
    return TemplateType.BusinessInitiated;
  } else {
    // case: either business or user-initiated filter is selected
    if (templateTypeFilter.length === 1) return templateTypeFilter[0];

    // case: filter is cleared or both options are selected
    // which are both equivalent to filtering on "nothing"
    return undefined;
  }
};
